import { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';
import { useActorRef, useSelector } from '@xstate/react';

import { OrderPaymentStatus } from '@sg/graphql-schema';
import { usePaymentStatePollingMachine } from '@sg/shared-fe-features-ordering';

import { clearLocalStorageOrderId, getOrderPaymentStateConfig } from '@order/features-ordering';
import { useNotifyStripeCancel, useNotifyStripeError } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  noopConfirmHandler,
  useCancelPaymentActor,
  useConfirmSweetpassPaymentActor,
  useFetchPaymentInfoActor,
  useFetchPaymentStateActor,
} from '../../machines/sweetpass-checkout-machine/actors';
import { sweetpassCheckoutMachine, type SweetpassCheckoutMachineImplementations } from '../../machines/sweetpass-checkout-machine/sweetpass-checkout.machine'; // prettier-ignore
import { logger } from '../../machines/sweetpass-checkout-machine/setup/sweetpass-checkout.machine.logger';
import { type ConfirmStripe } from '../../machines/sweetpass-checkout-machine/setup/sweetpass-checkout.machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Orchestrates the sweetpass purchase in two machines:
 *
 * - The sweetpass checkout machine loads the payment setup and confirms the payment with Stripe.
 * - The shared payment state polling machine tracks the confirmed payment until the backend
 *   fulfills or fails it, offering to cancel it when it is delayed.
 */
export const useSweetpassCheckoutMachine = (
  input: UseSweetpassCheckoutMachineInput,
  implementations?: SweetpassCheckoutMachineImplementations,
) => {
  const { sweetpassId, amount, onPurchaseSucceeded } = input;

  // ─── Actors ──────────────────────────────────────────────────────────

  const { fetchPaymentInfo } = useFetchPaymentInfoActor();
  const { confirmPayment } = useConfirmSweetpassPaymentActor();
  const { fetchPaymentState } = useFetchPaymentStateActor();
  const { cancelPayment } = useCancelPaymentActor();

  // ─── Feedback ────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const notifyStripeError = useNotifyStripeError();
  const notifyStripeCancel = useNotifyStripeCancel();

  // ─── Payment State Polling Machine ───────────────────────────────────

  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config');

  const {
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
  } = getOrderPaymentStateConfig(orderPaymentStateConfig);

  const onPaymentFulfilled = useCallback<PaymentStatePollingAction>(
    ({ context }) => {
      logger.info('Successfully purchased a sweetpass', {
        sweetpassId,
        paymentIntentId: context.paymentState.paymentIntentId,
      });
      addNoticeBanner({ text: formatMessage(messages.fulfilled), palette: 'success' });
      onPurchaseSucceeded();
    },
    [sweetpassId, addNoticeBanner, formatMessage, onPurchaseSucceeded],
  );

  const onPaymentStateError = useCallback<PaymentStatePollingAction>(
    ({ context }) => {
      const errorMessage = context.paymentState.paymentStateError ?? undefined;

      logger.error('Failed to fulfill a sweetpass payment', { sweetpassId, errorMessage });
      notifyStripeError(errorMessage, { canShowErrorMessage: true });
    },
    [sweetpassId, notifyStripeError],
  );

  const onPollingPaymentCanceled = useCallback<PaymentStatePollingAction>(() => {
    logger.info('Delayed sweetpass payment canceled', { sweetpassId });
    notifyStripeCancel();
  }, [sweetpassId, notifyStripeCancel]);

  const {
    startPolling,
    createPayment,
    requestPaymentCancelation,
    isPollingPayment,
    isPaymentDelayed,
    isCancelingPayment,
  } = usePaymentStatePollingMachine({
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
    fetchPaymentState,
    cancelPayment,
    clearPendingPayment: clearLocalStorageOrderId,
    incrementPurchaseAttempt: noop, // handled by the checkout machine
    onPaymentStateError,
    onPaymentCanceled: onPollingPaymentCanceled,
    onPaymentFulfilled,
  });

  // ─── Checkout Machine ────────────────────────────────────────────────

  // Stripe calls the intent config's confirm handler, which forwards to this ref.
  const stripeConfirmHandlerRef = useRef(noopConfirmHandler);

  const actorRef = useActorRef(
    sweetpassCheckoutMachine.provide({
      actors: {
        fetchPaymentInfo,
        confirmPayment,
        ...implementations?.actors,
      },
      actions: {
        onPendingPaymentFound(_args, params) {
          if (!params.hasPendingPayment) return;

          startPolling(params.orderId);
        },
        onPaymentConfirmed({ context }, params) {
          if (params.result !== 'confirmed') return;

          logger.info('Sweetpass payment confirmed', {
            sweetpassId: context.sweetpassId,
            paymentIntentId: params.paymentIntentId,
          });

          createPayment({
            paymentIntentId: params.paymentIntentId,
            paymentStateExpiration: Date.now(),
            paymentStateStatus: OrderPaymentStatus.Created,
            paymentStateError: undefined,
          });
          startPolling(params.orderId);
        },
        onFailedToLoad({ context }, params) {
          logger.error('Failed to fetch the sweetpass payment info', {
            sweetpassId: context.sweetpassId,
            errorMessage: params.errorMessage,
          });
        },
        onAlreadyPurchased({ context }) {
          logger.warn('Sweetpass was already purchased', { sweetpassId: context.sweetpassId });
          addNoticeBanner({ text: formatMessage(messages.alreadyPurchased), palette: 'neutral' });
          onPurchaseSucceeded();
        },
        onPaymentCanceled({ context }) {
          logger.info('Sweetpass payment canceled', { sweetpassId: context.sweetpassId });
          notifyStripeCancel();
        },
        onPaymentFailed({ context }, params) {
          logger.error('Failed to purchase a sweetpass', {
            sweetpassId: context.sweetpassId,
            errorMessage: params.errorMessage,
          });
          notifyStripeError(params.errorMessage, { canShowErrorMessage: true });
        },
        ...implementations?.actions,
      },
      guards: {
        ...implementations?.guards,
      },
    }),
    {
      input: {
        sweetpassId,
        amount,
        stripeConfirmHandlerRef,
      },
    },
  );

  // ─── State ───────────────────────────────────────────────────────────

  const isLoading = useSelector(actorRef, (snapshot) => snapshot.matches('loading') || snapshot.matches('checkingPendingPayment')); // prettier-ignore
  const isPurchasing = useSelector(actorRef, (snapshot) => snapshot.matches('purchasing'));
  const hasFailedToLoad = useSelector(actorRef, (snapshot) => snapshot.matches('failedToLoad'));

  const purchaseAttempt = useSelector(actorRef, (snapshot) => snapshot.context.purchaseAttempt);
  const stripeFailedToLoad = useSelector(actorRef, (snapshot) => snapshot.context.stripeFailedToLoad); // prettier-ignore
  const stripeSession = useSelector(actorRef, (snapshot) => snapshot.context.stripeSession);
  const stripeIntentConfig = useSelector(actorRef, (snapshot) => snapshot.context.stripeIntentConfig); // prettier-ignore
  const stripeElementConfig = useSelector(actorRef, (snapshot) => snapshot.context.stripeElementConfig); // prettier-ignore

  // ─── Commands ────────────────────────────────────────────────────────

  const purchase = useCallback(() => {
    actorRef.send({ type: 'PURCHASE' });
  }, [actorRef]);

  const setConfirmStripe = useCallback(
    (confirmStripe: ConfirmStripe) => {
      actorRef.send({ type: 'SET_CONFIRM_STRIPE', params: { confirmStripe } });
    },
    [actorRef],
  );

  const setStripeFailedToLoad = useCallback(() => {
    actorRef.send({ type: 'STRIPE_FAILED_TO_LOAD' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLoading,
    isPurchasing,
    isConfirmingPayment: isPollingPayment,
    isPaymentDelayed,
    isCancelingPayment,
    hasFailedToLoad,
    purchaseAttempt,
    stripeFailedToLoad,
    stripeSession,
    stripeIntentConfig,
    stripeElementConfig,
    purchase,
    cancelPayment: requestPaymentCancelation,
    setConfirmStripe,
    setStripeFailedToLoad,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const noop = () => {};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  fulfilled: {
    defaultMessage: 'Welcome to sweetpass!',
    description: 'Sweetpass | Checkout | Fulfilled',
  },
  alreadyPurchased: {
    defaultMessage: 'You had already purchased this sweetpass.',
    description: 'Sweetpass | Checkout | Already purchased error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassCheckoutMachineInput = {
  sweetpassId: string;
  amount: number;
  onPurchaseSucceeded: () => void;
};

type PaymentStatePollingAction = NonNullable<
  Parameters<typeof usePaymentStatePollingMachine>[0]['onPaymentFulfilled']
>;
